import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core';
import { CardHeader } from 'react-kanban';
import HourglassFullIcon from '@material-ui/icons/HourglassFull';
import WarningIcon from '@material-ui/icons/Warning';
import Popover from '@material-ui/core/Popover';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import CircularProgress from '@material-ui/core/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import RefreshIcon from '@material-ui/icons/Refresh';

import CustomerLink, { NURSE_REQUESTS_TAB } from '../Helpers/CustomerLink';
import { formatDate } from '../../helpers/tools';
import Location from '../Helpers/Location';
import { CustomerPropType } from '../../proptypes/customers';
import Typography from '../../Modules/mui/Components/Typography';
import Employee from '../Helpers/Employee';
import Agency from '../../Modules/agencies/components/Agency';
import Button from '../../Modules/buttons/components/Button';
import { useApi } from '../../Modules/api/components/ApiProvider';
import { NURSE_REQUEST_NOTIFICATION_RESOURCE } from '../../Modules/api/resources';
import { useSnackbar } from '../../Modules/snackbar/hooks';
import useRefreshCareNeedCard from '../../Modules/kanban/useRefreshCareNeedCard';

// Fixed, single-line height of the failed-mail banner (incl. its bottom
// margin). The virtualized Einsatz-Dashboard doesn't measure cards, it uses
// cardHeight() from careNeedDashboard.js - which adds exactly this. A banner
// whose text wrapped onto a second line used to grow past that estimate and
// eat the gap to the next card, so the banner is now clamped to one line.
const FAILED_MAIL_BANNER_BOX_HEIGHT = 24;
const FAILED_MAIL_BANNER_MARGIN_BOTTOM = 4;
export const FAILED_MAIL_BANNER_HEIGHT =
    FAILED_MAIL_BANNER_BOX_HEIGHT + FAILED_MAIL_BANNER_MARGIN_BOTTOM;

const useStyles = makeStyles((theme) => ({
    title: {
        color: theme.palette.text.primary,
    },

    icon: {
        position: 'absolute',
        right: theme.spacing(2),
        fontSize: 16,
    },

    // Sits inline in the "#<customer id>" line - no padding, so the card's
    // fixed (virtualized) height in careNeedDashboard.js stays the same.
    idRow: {
        display: 'flex',
        alignItems: 'center',
    },
    refreshButton: {
        padding: 0,
        marginLeft: theme.spacing(0.5),
    },
    refreshIcon: {
        fontSize: 16,
    },

    // A normal-flow row above the title, not an absolutely positioned
    // overlay - react-kanban's CardHeader positions title/aside/subheader
    // against each other in ways that left no corner free of something else
    // (the date, the employee name, ...). Its own row avoids that entirely.
    failedMailBanner: {
        display: 'flex',
        alignItems: 'center',
        boxSizing: 'border-box',
        height: FAILED_MAIL_BANNER_BOX_HEIGHT,
        padding: theme.spacing(0, 1),
        marginBottom: FAILED_MAIL_BANNER_MARGIN_BOTTOM,
        overflow: 'hidden',
        backgroundColor: theme.palette.error.light || '#fdecea',
        color: theme.palette.error.dark || theme.palette.error.main,
        cursor: 'pointer',
        borderRadius: 4,
    },

    // Shown instead of the (red, "something is wrong") style above while
    // every notification is still pending/processing - nothing has actually
    // failed yet, this is purely "a mail is taking a while", so it shouldn't
    // read as an alarm. Found necessary because a slow mailer can leave a
    // mail at email_queue.status='processing' for minutes (Microsoft Graph
    // throttling, or the backoff waits between SendQueuedEmail's own retries)
    // with nothing shown at all otherwise - easy to mistake for success and
    // only notice once an agency calls asking why they got nothing.
    failedMailBannerPending: {
        backgroundColor: theme.palette.info.light || '#e3f2fd',
        color: theme.palette.info.dark || theme.palette.info.main,
    },

    failedMailBannerIcon: {
        fontSize: 16,
        flexShrink: 0,
        marginRight: theme.spacing(0.5),
    },

    failedMailBannerText: {
        minWidth: 0,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },

    popoverContent: {
        // Wide enough for the bulk-action buttons on one line; a care need
        // can have dozens of open agencies (e.g. customer #42360, 41), so a
        // single-column 360px popover isn't the constraint here - the list
        // height is (see popoverList below).
        width: 400,
        padding: theme.spacing(1),
    },

    popoverHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(0.5),
    },

    bulkActions: {
        display: 'flex',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(0.5),
    },

    // Unbounded on a customer with many open agencies this would render as
    // a popover taller than the screen - found via customer #42360 (41 open
    // BK-Anfragen, so potentially as many failed notifications at once).
    popoverList: {
        maxHeight: 400,
        overflowY: 'auto',
    },

    // ListItemSecondaryAction positions itself absolutely at the item's
    // vertical center, which overlaps the text as soon as the secondary
    // line (type + error, often long) wraps past one line - stack the
    // actions below the text in normal flow instead.
    notificationItem: {
        display: 'block',
    },

    notificationActions: {
        marginTop: theme.spacing(0.5),
    },
}));

const NOTIFICATION_TYPE_LABELS = {
    close_notification: 'Absage (Personalvorschlag angenommen)',
    cancelation: 'Absage (Alle BK-Anfragen absagen)',
    create: 'Anfrage (BK-Anfrage versenden)',
    suggestion: 'Vorschlag (BK-Vorschlag versenden)',
};

// 'sent' never reaches here (the backend prefetch already excludes it - see
// PrefetchesFailedMailNotifications.php), so this only has to distinguish
// "still going" from "actually failed".
const isPending = (notification) =>
    notification.status === 'pending' || notification.status === 'processing';

/**
 * Phase 10 of docs/plans/email-queue-database-migration.md: shown on every
 * Einsatz-Dashboard card type via this one shared header, so it follows a
 * care need wherever it currently renders (e.g. "Alle BK-Anfragen absagen"
 * keeps it on BK-Anfragen, while accepting a suggestion and closing the
 * others typically moves it to Einsatz-Abwicklung/Visiten instead).
 */
const FailedMailNotificationsBadge = ({ notifications, onChange }) => {
    const classes = useStyles();
    const api = useApi();
    const { enqueueSnackbar } = useSnackbar();
    const [anchorEl, setAnchorEl] = useState(null);
    const [busyId, setBusyId] = useState(null);
    const [bulkBusy, setBulkBusy] = useState(false);
    const [items, setItems] = useState(notifications);

    // The parent re-fetches on its own schedule (board polling/reload) - keep
    // showing the latest prop value whenever that happens, but let local
    // retry/dismiss actions update the list immediately in between without
    // waiting for that refetch.
    useEffect(() => {
        setItems(notifications);
    }, [notifications]);

    const handleOpen = useCallback((event) => setAnchorEl(event.currentTarget), []);
    const handleClose = useCallback(() => setAnchorEl(null), []);

    const removeItem = useCallback((id) => {
        setItems((current) => current.filter((item) => item.id !== id));
    }, []);

    const handleRetry = useCallback(
        async (notification) => {
            setBusyId(notification.id);
            try {
                // Always goes through the notification, not
                // EMAIL_QUEUE_RESOURCE directly - NurseRequestNotificationController::retry()
                // re-points this notification's emailQueueId at whatever new
                // email_queue row the retry produces (it never mutates the
                // old one). Calling the queue resource directly here was the
                // bug (found 2026-10-01, customer 42042): a further failure
                // on retry had nothing pointing at it at all.
                //
                // The response is unwrapped twice: `.data` is
                // createRestMethod's own envelope around the backend's
                // {success, data, message} body (see
                // AppBaseController::sendResponse()) - without it `updated`
                // was always undefined, so a real retry's result never
                // reached this component at all and the badge only ever
                // caught up via the next full board refetch (found
                // 2026-10-01 alongside the email_queue_id: null bug above).
                const {
                    data: { notification: updated },
                } = await api[NURSE_REQUEST_NOTIFICATION_RESOURCE].retry({
                    id: notification.id,
                });
                if (updated && updated.status === 'sent') {
                    removeItem(notification.id);
                } else if (updated) {
                    // Still pending/processing, or failed again under the new
                    // row - keep showing it, with the now-current
                    // emailQueueId/lastError, so a second "Erneut senden"
                    // targets the right row instead of the stale one.
                    setItems((current) =>
                        current.map((item) =>
                            item.id === notification.id ? { ...item, ...updated } : item
                        )
                    );
                }
                enqueueSnackbar('Mail wird erneut versendet.', { variant: 'success' });
                if (onChange) {
                    onChange();
                }
            } catch (error) {
                console.error('Failed to retry failed mail notification', notification.id, error);
                enqueueSnackbar('Erneuter Versand fehlgeschlagen.', { variant: 'error' });
            } finally {
                setBusyId(null);
            }
        },
        [api, enqueueSnackbar, onChange, removeItem]
    );

    const handleDismiss = useCallback(
        async (notification) => {
            setBusyId(notification.id);
            try {
                await api[NURSE_REQUEST_NOTIFICATION_RESOURCE].dismiss({ id: notification.id });
                removeItem(notification.id);
                if (onChange) {
                    onChange();
                }
            } catch (error) {
                console.error('Failed to dismiss failed mail notification', notification.id, error);
                enqueueSnackbar('Konnte nicht als erledigt markiert werden.', { variant: 'error' });
            } finally {
                setBusyId(null);
            }
        },
        [api, enqueueSnackbar, onChange, removeItem]
    );

    // Bulk variants of the two actions above, for care needs with many open
    // agencies at once (e.g. customer #42360, 41) where resolving one at a
    // time isn't practical. Each notification is retried/dismissed
    // independently via Promise.allSettled - one already-resolved or
    // mid-backoff row failing (e.g. "Only a failed send can be retried")
    // doesn't block the rest.
    const handleRetryAll = useCallback(async () => {
        // Pending/processing items aren't actually failed yet - retrying one
        // would just throw "Only a failed send can be retried" and count as
        // a spurious failure in the summary below, so leave them alone.
        const targets = items.filter((notification) => !isPending(notification));
        if (targets.length === 0) {
            enqueueSnackbar('Keine fehlgeschlagenen Mails zum erneuten Versenden.', {
                variant: 'info',
            });
            return;
        }
        setBulkBusy(true);
        try {
            const results = await Promise.allSettled(
                targets.map((notification) =>
                    api[NURSE_REQUEST_NOTIFICATION_RESOURCE].retry({ id: notification.id })
                )
            );

            const sentIds = new Set();
            const updatesById = new Map();
            let succeeded = 0;
            let failed = 0;
            results.forEach((result, index) => {
                const notification = targets[index];
                if (result.status === 'fulfilled') {
                    succeeded += 1;
                    // Same response.data.data unwrap as handleRetry above.
                    const updated = result.value.data.notification;
                    if (updated && updated.status === 'sent') {
                        sentIds.add(notification.id);
                    } else if (updated) {
                        updatesById.set(notification.id, updated);
                    }
                } else {
                    failed += 1;
                    console.error(
                        'Failed to retry failed mail notification',
                        notification.id,
                        result.reason
                    );
                }
            });

            setItems((current) =>
                current
                    .filter((item) => !sentIds.has(item.id))
                    .map((item) =>
                        updatesById.has(item.id) ? { ...item, ...updatesById.get(item.id) } : item
                    )
            );

            if (failed === 0) {
                enqueueSnackbar(
                    `${succeeded} Mail${succeeded === 1 ? '' : 's'} werden erneut versendet.`,
                    { variant: 'success' }
                );
            } else {
                enqueueSnackbar(
                    `${succeeded} von ${targets.length} Mails werden erneut versendet, ${failed} fehlgeschlagen.`,
                    { variant: 'warning' }
                );
            }
            if (onChange) {
                onChange();
            }
        } finally {
            setBulkBusy(false);
        }
    }, [api, items, enqueueSnackbar, onChange]);

    const handleDismissAll = useCallback(async () => {
        const targets = items;
        if (targets.length === 0) {
            return;
        }
        // Dismissing just hides the badge, it doesn't fix anything - at
        // this scale (potentially dozens at once) that's an easy way to
        // accidentally mask real unresolved failures, so confirm first
        // (window.confirm is the established pattern for this in this
        // codebase, e.g. MapResults.jsx).
        if (
            !window.confirm(
                `${targets.length} nicht zugestellte Mail${
                    targets.length === 1 ? '' : 's'
                } wirklich als erledigt markieren?`
            )
        ) {
            return;
        }
        setBulkBusy(true);
        try {
            const results = await Promise.allSettled(
                targets.map((notification) =>
                    api[NURSE_REQUEST_NOTIFICATION_RESOURCE].dismiss({ id: notification.id })
                )
            );

            const dismissedIds = new Set();
            let failed = 0;
            results.forEach((result, index) => {
                const notification = targets[index];
                if (result.status === 'fulfilled') {
                    dismissedIds.add(notification.id);
                } else {
                    failed += 1;
                    console.error(
                        'Failed to dismiss failed mail notification',
                        notification.id,
                        result.reason
                    );
                }
            });

            setItems((current) => current.filter((item) => !dismissedIds.has(item.id)));

            if (failed === 0) {
                enqueueSnackbar(
                    `${dismissedIds.size} Mail${
                        dismissedIds.size === 1 ? '' : 's'
                    } als erledigt markiert.`,
                    { variant: 'success' }
                );
            } else {
                enqueueSnackbar(
                    `${dismissedIds.size} von ${targets.length} als erledigt markiert, ${failed} fehlgeschlagen.`,
                    { variant: 'warning' }
                );
            }
            if (onChange) {
                onChange();
            }
        } finally {
            setBulkBusy(false);
        }
    }, [api, items, enqueueSnackbar, onChange]);

    if (!items || items.length === 0) {
        return null;
    }

    const open = Boolean(anchorEl);
    const failedItems = items.filter((notification) => !isPending(notification));
    const pendingItems = items.filter(isPending);
    // Any actual failure makes this an alarm, regardless of how many are
    // still only pending - the "nothing shown until it's already failed"
    // gap is only closed for the all-pending case, this doesn't change the
    // weight of a real failure.
    const hasFailed = failedItems.length > 0;

    // Kept short so it fits on one line even in a narrow column (see
    // FAILED_MAIL_BANNER_HEIGHT) - the full sentence is in the tooltip.
    const mails = (count) => `${count} Mail${count === 1 ? '' : 's'}`;
    let bannerText;
    if (hasFailed && pendingItems.length > 0) {
        bannerText = `${mails(failedItems.length)} nicht zugestellt, ${
            pendingItems.length
        } in Versand`;
    } else if (hasFailed) {
        bannerText = `${mails(failedItems.length)} nicht zugestellt`;
    } else {
        bannerText = `${mails(pendingItems.length)} in Versand`;
    }
    const bannerTooltip = `${bannerText} - klicken zum Anzeigen`;

    return (
        <>
            <div
                className={classNames(classes.failedMailBanner, {
                    [classes.failedMailBannerPending]: !hasFailed,
                })}
                onClick={handleOpen}
                role="button"
                tabIndex={0}
                onKeyPress={handleOpen}
                title={bannerTooltip}
            >
                {hasFailed ? (
                    <WarningIcon className={classes.failedMailBannerIcon} fontSize="small" />
                ) : (
                    <HourglassFullIcon className={classes.failedMailBannerIcon} fontSize="small" />
                )}
                <Typography variant="body2" className={classes.failedMailBannerText}>
                    {bannerText}
                </Typography>
            </div>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                onClick={(event) => event.stopPropagation()}
            >
                <div className={classes.popoverContent}>
                    <div className={classes.popoverHeader}>
                        <Typography variant="subtitle2">Mails ({items.length})</Typography>
                        {bulkBusy && <CircularProgress size={16} />}
                    </div>
                    {items.length > 1 && (
                        <div className={classes.bulkActions}>
                            <Button
                                size="small"
                                disabled={bulkBusy || busyId !== null}
                                onClick={handleRetryAll}
                            >
                                Alle erneut senden
                            </Button>
                            <Button
                                size="small"
                                disabled={bulkBusy || busyId !== null}
                                onClick={handleDismissAll}
                            >
                                Alle als erledigt markieren
                            </Button>
                        </div>
                    )}
                    <List dense className={classes.popoverList}>
                        {items.map((notification) => (
                            <ListItem
                                key={notification.id}
                                divider
                                disableGutters
                                className={classes.notificationItem}
                            >
                                <ListItemText
                                    primary={<Agency agencyId={notification.agencyId} />}
                                    secondary={
                                        <>
                                            {NOTIFICATION_TYPE_LABELS[notification.type] ||
                                                notification.type}
                                            <br />
                                            {isPending(notification)
                                                ? 'Wird noch versendet...'
                                                : notification.lastError}
                                        </>
                                    }
                                />
                                <Grid
                                    container
                                    justify="flex-end"
                                    spacing={1}
                                    alignItems="center"
                                    className={classes.notificationActions}
                                >
                                    {busyId === notification.id || bulkBusy ? (
                                        <Grid item>
                                            <CircularProgress size={20} />
                                        </Grid>
                                    ) : isPending(notification) ? (
                                        <>
                                            <Grid item>
                                                <CircularProgress size={16} />
                                            </Grid>
                                            <Grid item>
                                                <Button
                                                    size="small"
                                                    disabled={busyId !== null}
                                                    onClick={() => handleDismiss(notification)}
                                                >
                                                    Erledigt
                                                </Button>
                                            </Grid>
                                        </>
                                    ) : (
                                        <>
                                            <Grid item>
                                                <Button
                                                    size="small"
                                                    disabled={busyId !== null}
                                                    onClick={() => handleRetry(notification)}
                                                >
                                                    Erneut senden
                                                </Button>
                                            </Grid>
                                            <Grid item>
                                                <Button
                                                    size="small"
                                                    disabled={busyId !== null}
                                                    onClick={() => handleDismiss(notification)}
                                                >
                                                    Erledigt
                                                </Button>
                                            </Grid>
                                        </>
                                    )}
                                </Grid>
                            </ListItem>
                        ))}
                    </List>
                </div>
            </Popover>
        </>
    );
};

FailedMailNotificationsBadge.propTypes = {
    notifications: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number.isRequired,
            type: PropTypes.string,
            agencyId: PropTypes.number,
            emailQueueId: PropTypes.number,
            status: PropTypes.string,
            lastError: PropTypes.string,
        })
    ),
    onChange: PropTypes.func,
};

FailedMailNotificationsBadge.defaultProps = {
    notifications: [],
    onChange: null,
};

/**
 * Reloads just this one card (see useRefreshCareNeedCard) - e.g. to check
 * whether a BK-Vorschlag whose send response got lost was in fact sent: the
 * card then moves to "BK-Vorgestellt" and shows the mail's delivery status.
 * Manual on purpose, to keep traffic down - no polling.
 */
const CareNeedCardRefreshButton = ({ careNeedId }) => {
    const classes = useStyles();
    const refreshCareNeedCard = useRefreshCareNeedCard();
    const { enqueueSnackbar } = useSnackbar();
    const [busy, setBusy] = useState(false);

    // The card unmounts here whenever the refresh moves it to another column.
    const mountedRef = useRef(true);
    useEffect(
        () => () => {
            mountedRef.current = false;
        },
        []
    );

    const handleClick = useCallback(
        async (event) => {
            event.stopPropagation();
            setBusy(true);
            try {
                await refreshCareNeedCard(careNeedId);
            } catch (error) {
                console.error('CareNeedCardRefreshButton: refresh failed', careNeedId, error);
                enqueueSnackbar('Karte konnte nicht aktualisiert werden.', { variant: 'error' });
            } finally {
                if (mountedRef.current) {
                    setBusy(false);
                }
            }
        },
        [careNeedId, refreshCareNeedCard, enqueueSnackbar]
    );

    return (
        <Tooltip title="Diese Karte aktualisieren">
            <span>
                <IconButton
                    className={classes.refreshButton}
                    size="small"
                    onClick={handleClick}
                    disabled={busy}
                >
                    {busy ? (
                        <CircularProgress size={14} />
                    ) : (
                        <RefreshIcon className={classes.refreshIcon} />
                    )}
                </IconButton>
            </span>
        </Tooltip>
    );
};

CareNeedCardRefreshButton.propTypes = {
    careNeedId: PropTypes.number.isRequired,
};

const CareNeedHeader = ({
    customer,
    urgent,
    date,
    targetTab,
    onClick,
    external,
    waiting,
    showId,
    careNeedId,
    failedMailNotifications,
    onFailedMailNotificationsChange,
}) => {
    const classes = useStyles();

    return (
        <>
            <FailedMailNotificationsBadge
                notifications={failedMailNotifications}
                onChange={onFailedMailNotificationsChange}
            />
            <CardHeader
                title={
                    <CustomerLink
                        customer={customer}
                        tab={targetTab}
                        className={classes.title}
                        onClick={onClick}
                        // damit lange Namen (mit aside) nicht für mehr Zeilen sorgen
                        style={{
                            display: 'block',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            position: `${date ? 'absolute' : 'relative'}`,
                            maxWidth: `${date ? 'calc(100% - 95px)' : '100%'}`,
                        }}
                        title={customer && customer.contact && customer.contact.fullname}
                    >
                        {customer && customer.contact && customer.contact.fullname}
                    </CustomerLink>
                }
                aside={date ? formatDate(date) : ''}
                asideColor={urgent ? 'error' : null}
                subheader={
                    <>
                        {waiting && <HourglassFullIcon className={classes.icon} />}
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                {!external && customer && customer.location && (
                                    <Typography variant="body2" ellipsis>
                                        Standort: <Location id={customer.location} />
                                    </Typography>
                                )}
                            </Grid>
                            <Grid
                                item
                                xs={6}
                                justify="flex-end"
                                direction="column"
                                alignItems="flex-end"
                                container
                            >
                                {!external && customer && customer.employee && (
                                    <Typography variant="body2" ellipsis>
                                        <Employee id={customer.employee} />
                                    </Typography>
                                )}
                            </Grid>
                        </Grid>
                        {showId && customer && (
                            <div className={classes.idRow}>
                                <Typography variant="body2">#{customer.id}</Typography>
                                {careNeedId && (
                                    <CareNeedCardRefreshButton careNeedId={careNeedId} />
                                )}
                            </div>
                        )}
                    </>
                }
            />
        </>
    );
};

CareNeedHeader.propTypes = {
    customer: CustomerPropType,
    urgent: PropTypes.bool,
    date: PropTypes.string,
    targetTab: PropTypes.string,
    onClick: PropTypes.func,
    external: PropTypes.bool,
    waiting: PropTypes.bool,
    showId: PropTypes.bool,
    // Set on the care need cards (BK-Bedarf/-Anfragen/-Vorgestellt) - shows
    // the per-card refresh icon next to the customer id.
    careNeedId: PropTypes.number,
    // Phase 10 of docs/plans/email-queue-database-migration.md.
    failedMailNotifications: PropTypes.array,
    onFailedMailNotificationsChange: PropTypes.func,
};

CareNeedHeader.defaultProps = {
    customer: null,
    urgent: false,
    date: null,
    targetTab: NURSE_REQUESTS_TAB,
    onClick: null,
    external: false,
    waiting: false,
    showId: false,
    careNeedId: null,
    failedMailNotifications: [],
    onFailedMailNotificationsChange: null,
};

export default CareNeedHeader;
